import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { FileWarning, Plus, Trash2, Edit, X, AlertCircle, CheckCircle2, Clock } from "lucide-react";

const STATUS_STYLES = {
  expired: { bg: "rgba(220,38,38,0.15)", color: "#fca5a5", label: "EXPIRAT", icon: AlertCircle },
  critical: { bg: "rgba(234,88,12,0.15)", color: "#fdba74", label: "SUB 7 ZILE", icon: AlertCircle },
  warning: { bg: "rgba(234,179,8,0.15)", color: "#fde68a", label: "SUB 30 ZILE", icon: Clock },
  ok: { bg: "rgba(34,197,94,0.10)", color: "#86efac", label: "OK", icon: CheckCircle2 },
};

export default function AdminDocuments() {
  const [items, setItems] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [horizon, setHorizon] = useState(90);

  // Per-car edit modal
  const [editing, setEditing] = useState(null); // { car_id, doc?: {...} }

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [a, s] = await Promise.all([
        api.get(`/admin/documents/expiring?days_ahead=${horizon}`),
        api.get(`/admin/settings`).catch(() => ({ data: {} })),
      ]);
      setItems(Array.isArray(a.data) ? a.data : []);
      setDocTypes(s.data?.document_types || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [horizon]);

  const removeDoc = async (item) => {
    if (!window.confirm("Sterge documentul?")) return;
    try {
      await api.delete(`/admin/cars/${item.car_id}/documents/${item.id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-documents">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>Dashboard</div>
          <h1 className="font-serif text-3xl sm:text-4xl flex items-center gap-3">
            <FileWarning className="w-7 h-7" style={{ color: "var(--brand-primary)" }} />
            Documente expirare
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--brand-text-secondary)" }}>
            Alerte automate prin email cu 14, 7 si 1 zi inainte de expirare. Documentele se gestioneaza din pagina fiecarei masini.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="label-eyebrow">Orizont:</label>
          <select value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} className="brand-input py-1.5" data-testid="horizon-select">
            <option value={30}>30 zile</option>
            <option value={60}>60 zile</option>
            <option value={90}>90 zile</option>
            <option value={365}>1 an</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 text-sm flex items-center gap-2" style={{ background: "rgba(220,38,38,0.15)", color: "#fca5a5" }}>
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading && (
        <div className="text-center py-6 text-sm" style={{ color: "var(--brand-text-secondary)" }}>Se incarca...</div>
      )}

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="docs-table">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--brand-border)" }}>
              <th className="text-left p-4 label-eyebrow">Masina</th>
              <th className="text-left p-4 label-eyebrow">Document</th>
              <th className="text-left p-4 label-eyebrow">Expira la</th>
              <th className="text-left p-4 label-eyebrow">Status</th>
              <th className="text-left p-4 label-eyebrow">Note</th>
              <th className="text-right p-4 label-eyebrow">Actiuni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => {
              const meta = docTypes.find((x) => x.key === d.doc_type) || {};
              const label = d.custom_label_ro || meta.label_ro || d.doc_type;
              const s = STATUS_STYLES[d.status] || STATUS_STYLES.ok;
              const Icon = s.icon;
              return (
                <tr key={d.id} className="border-b" style={{ borderColor: "var(--brand-border)" }} data-testid={`doc-row-${d.id}`}>
                  <td className="p-4">
                    <div className="font-medium">{d.car_name || "—"}</div>
                    <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{d.car_brand_model}</div>
                  </td>
                  <td className="p-4">{label}</td>
                  <td className="p-4 font-mono text-xs">{d.expires_at}</td>
                  <td className="p-4">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs uppercase tracking-widest" style={{ background: s.bg, color: s.color }}>
                      <Icon className="w-3 h-3" />
                      {s.label} · {d.days_until < 0 ? `(acum ${-d.days_until}z)` : `${d.days_until}z`}
                    </span>
                  </td>
                  <td className="p-4 text-xs" style={{ color: "var(--brand-text-secondary)" }}>{d.notes || "—"}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => setEditing({ car_id: d.car_id, doc: d })} className="btn-ghost mr-2" data-testid={`edit-doc-${d.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeDoc(d)} className="btn-ghost" data-testid={`del-doc-${d.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-sm" style={{ color: "var(--brand-text-secondary)" }}>
                Niciun document in acest orizont. Adauga documente din pagina fiecarei masini.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <DocEditModal
          item={editing}
          docTypes={docTypes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DocEditModal({ item, docTypes, onClose, onSaved }) {
  const doc = item.doc || {};
  const [form, setForm] = useState({
    doc_type: doc.doc_type || (docTypes[0]?.key || "rca"),
    expires_at: doc.expires_at || "",
    notes: doc.notes || "",
    custom_label_ro: doc.custom_label_ro || "",
    custom_label_en: doc.custom_label_en || "",
  });
  const [err, setErr] = useState("");

  const isCustom = form.doc_type === "__custom";

  const save = async () => {
    if (!form.expires_at) { setErr("Data de expirare este obligatorie"); return; }
    if (isCustom && !form.custom_label_ro) { setErr("Label custom RO obligatoriu pentru tip personalizat"); return; }
    try {
      const payload = {
        doc_type: isCustom ? `custom_${Date.now()}` : form.doc_type,
        expires_at: form.expires_at,
        notes: form.notes,
        custom_label_ro: form.custom_label_ro,
        custom_label_en: form.custom_label_en,
      };
      if (doc.id) await api.put(`/admin/cars/${item.car_id}/documents/${doc.id}`, payload);
      else await api.post(`/admin/cars/${item.car_id}/documents`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-surface p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="doc-edit-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-xl">{doc.id ? "Editeaza document" : "Document nou"}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-2">Tip document</label>
            <select className="brand-input" value={form.doc_type} onChange={(e) => setForm({...form, doc_type: e.target.value})} data-testid="doc-type">
              {docTypes.map((t) => <option key={t.key} value={t.key}>{t.label_ro}</option>)}
              <option value="__custom">+ Tip personalizat...</option>
            </select>
          </div>
          {isCustom && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-2">Label RO</label>
                <input className="brand-input" value={form.custom_label_ro} onChange={(e) => setForm({...form, custom_label_ro: e.target.value})} placeholder="ex. Revizie" data-testid="doc-label-ro" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Label EN</label>
                <input className="brand-input" value={form.custom_label_en} onChange={(e) => setForm({...form, custom_label_en: e.target.value})} placeholder="e.g. Service" />
              </div>
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-2">Data expirare *</label>
            <input type="date" className="brand-input" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} data-testid="doc-expires" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Note</label>
            <textarea className="brand-input" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Detalii suplimentare..." data-testid="doc-notes" />
          </div>
        </div>
        {err && <div className="text-sm mt-3" style={{ color: "var(--brand-primary)" }}>{err}</div>}
        <div className="flex gap-3 mt-5">
          <button onClick={save} className="btn-gold-filled flex-1" data-testid="save-doc">Salveaza</button>
          <button onClick={onClose} className="btn-ghost">Anuleaza</button>
        </div>
      </div>
    </div>
  );
}
